"use client";

import { CenterOptions } from "@/Helpers/CenterOptions";
import { VideoSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement } from "react";
import HomeEightVideoSectionGenerated, {
  HomeEightVideoSectionGeneratedProps,
} from "../Sections/_generated/HomeEightVideoSection.generated";
import HomeFourVideoSectionGenerated, {
  HomeFourVideoSectionGeneratedProps,
} from "../Sections/_generated/HomeFourVideoSection.generated";
import HomeSixteenVideoSectionGenerated, {
  HomeSixteenVideoSectionGeneratedProps,
} from "../Sections/_generated/HomeSixteenVideoSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";
import useDoccureVideoModal from "./useDoccureVideoModal";

type GeneratedVideoRenderer = (props: VideoSectionProps) => ReactElement;

const VideoAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateVideoAppearance;

function getSectionImageUrl(props: VideoSectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(
  props: VideoSectionProps,
  fallback: string,
): string {
  return props.setting.Image?.AltText ?? fallback;
}

const GeneratedVideoSection: FC<VideoSectionProps> = (props) => {
  const videoModal = useDoccureVideoModal();
  const videoUrl = props.setting.Video?.Url;

  const generatedVideoRenderers: Record<string, GeneratedVideoRenderer> = {
    [VideoAppearance.Home4.key]: (sectionProps) => (
      <HomeFourVideoSectionGenerated
        {...buildGeneratedSectionProps<HomeFourVideoSectionGeneratedProps>(
          "HomeFourVideoSection",
          {
            motionSectionStyle: getSectionImageUrl(sectionProps)
              ? {
                  backgroundImage: `url(${getSectionImageUrl(sectionProps)})`,
                }
              : undefined,
            motionSectionDiv0Div0Link0LinkText:
              sectionProps.setting.ButtonText ?? undefined,
            motionSectionDiv0Div0Link0LinkProps: {
              onClick: (event) => {
                event.preventDefault();
                if (!videoUrl) {
                  return;
                }

                videoModal.show(videoUrl);
              },
            },
          },
        )}
      />
    ),
    [VideoAppearance.Home8.key]: (sectionProps) => (
      <HomeEightVideoSectionGenerated
        {...buildGeneratedSectionProps<HomeEightVideoSectionGeneratedProps>(
          "HomeEightVideoSection",
          {
            sectionImageWithBasePath0ImageSrc:
              getSectionImageUrl(sectionProps) ?? undefined,
            sectionImageWithBasePath0ImageAlt: getSectionImageAlt(
              sectionProps,
              "video",
            ),
            sectionLink0Button0ButtonDataText:
              sectionProps.setting.ButtonText ?? undefined,
            sectionLink0LinkProps: {
              onClick: (event) => {
                event.preventDefault();
                if (!videoUrl) {
                  return;
                }

                videoModal.show(videoUrl);
              },
            },
          },
        )}
      />
    ),
    [VideoAppearance.Home16.key]: (sectionProps) => (
      <HomeSixteenVideoSectionGenerated
        {...buildGeneratedSectionProps<HomeSixteenVideoSectionGeneratedProps>(
          "HomeSixteenVideoSection",
          {
            divDiv0ImageWithBasePath0ImageSrc:
              getSectionImageUrl(sectionProps) ?? undefined,
            divDiv0ImageWithBasePath0ImageAlt: getSectionImageAlt(
              sectionProps,
              "video",
            ),
            divDiv0Div0Button0ButtonAriaLabel:
              sectionProps.setting.ButtonText ?? undefined,
            divDiv0Div0Button0Div0DivDataText:
              sectionProps.setting.ButtonText ?? undefined,
            divDiv0Div0Button0ButtonProps: {
              onClick: () => {
                if (videoUrl) {
                  videoModal.show(videoUrl);
                }
              },
            },
          },
        )}
      />
    ),
  };

  return renderGeneratedSection(
    props,
    generatedVideoRenderers,
    VideoAppearance.Home4.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedVideoSection;
